<template>
  <v-app>
    <v-main>
      <v-container fluid>
        <div class="candidati">
          <!-- Header curat: icon + titlu + subtitle + CTA primar (stil de referință CRM) -->
          <div class="candidati__header">
            <div class="candidati__header-left">
              <v-icon class="candidati__header-icon" size="18">mdi-account-multiple-outline</v-icon>
              <h1 class="candidati__title">{{ $t("candidates.title") }}</h1>
              <span class="candidati__subtitle">{{ $t("candidates.subtitle") }}</span>
            </div>
            <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="openAddDialog">
              {{ $t("candidates.add") }}
            </v-btn>
          </div>

          <v-card variant="flat" class="candidati__panel">
            <CandidateListToolbar
              :search="search"
              :view-mode="viewMode"
              :view-items="viewItems"
              :active-chips="activeChips"
              :active-filters-count="activeFiltersCount"
              :results-count="filteredData.length"
              @update-search="search = $event"
              @update-view-mode="viewMode = $event"
              @open-filters="filtersDrawer = true"
              @remove-filter="removeFilterChip"
              @reset-filters="resetFilters"
            />

            <div class="pa-4">
              <!-- Vizualizare carduri -->
              <CandidateCardGrid
                v-if="viewMode === 'cards'"
                :items="filteredData"
                @open="goToCandidateDetails"
                @edit="openEditDialog"
                @delete="confirmDeleteCandidate"
              />

              <!-- Vizualizare tabel -->
              <CandidateTable
                v-else
                :items="filteredData"
                v-model:page="page"
                :items-per-page="itemsPerPage"
                @open="goToCandidateDetails"
              />
            </div>
          </v-card>

          <!-- Drawer cu filtre avansate -->
          <CandidateFiltersDrawer
            v-model="filtersDrawer"
            :filters="filters"
            :options-profession="optionsProfession"
            :options-specialization="optionsSpecialization"
            :options-looking-position="optionsLookingPosition"
            :options-cv-source="optionsCVSource"
            @apply="applyFilters"
            @reset="resetFilters"
          />
        </div>

        <!-- Dialog Adăugare/Editare Candidat -->
        <CandidateFormDialog
          v-model="editDialog"
          :is-edit="isEdit"
          :candidate="editCandidate"
          :companies="companies"
          :show-company-select="isAdmin && !isEdit"
          @save="onDialogSave"
        />

        <!-- Dialog confirmare ștergere (tastare DELETE pentru date critice) -->
        <v-dialog v-model="deleteDialog" max-width="480">
          <v-card>
            <v-card-title>{{ $t("candidates.delete.title") }}</v-card-title>
            <v-card-text>
              <p class="mb-4">{{ $t("candidates.delete.body") }}</p>
              <v-text-field
                v-model="deleteConfirmText"
                :label="$t('candidates.delete.typeToConfirm')"
                autofocus
                hide-details
                @keyup.enter="performDelete"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="closeDeleteDialog">{{ $t("candidates.delete.cancel") }}</v-btn>
              <v-btn
                color="error"
                :disabled="deleteConfirmText !== 'DELETE'"
                @click="performDelete"
              >
                {{ $t("candidates.delete.confirm") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Notificări non-intruzive -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" location="bottom right">
          {{ snackbarMessage }}
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import CandidateFiltersDrawer from "@/components/candidate/CandidateFiltersDrawer.vue";
import CandidateCardGrid from "@/components/candidate/CandidateCardGrid.vue";
import CandidateTable from "@/components/candidate/CandidateTable.vue";
import CandidateFormDialog from "@/components/candidate/CandidateFormDialog.vue";
import CandidateListToolbar from "@/components/candidate/CandidateListToolbar.vue";
import { createEmptyCandidate } from "@/Utile/candidateForm.mjs";
import { getRolesSetFromStorage } from "@/Utile/navigationDrawerAuth.mjs";

export default defineComponent({
  name: "Candidati",
  components: {
    CandidateFiltersDrawer,
    CandidateCardGrid,
    CandidateTable,
    CandidateFormDialog,
    CandidateListToolbar,
  },
  data() {
    return {
      candidatesData: [],
      editDialog: false,
      isEdit: false,
      editCandidate: createEmptyCandidate(),
      companies: [], // pentru selectorul de companie la adăugare (doar admin)
      search: "",
      viewMode: "cards",
      // ---- FILTRE ----
      filtersDrawer: false,
      filters: {
        profession: [],
        specialization: [],
        lookingPosition: [],
        cvSource: [],
        contacted: null, // null | true | false
        hasCV: null,     // null | true | false
      },
      syncingRoute: false,
      debounceTimer: null,
      applyingFromRoute: false,
      page: 1,
      itemsPerPage: 10,
      // Notificări non-intruzive
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      // Confirmare ștergere (tastare DELETE)
      deleteDialog: false,
      deleteId: null,
      deleteConfirmText: "",
    };
  },
  computed: {
    // Doar admin alege compania la adăugarea unui candidat; hr/miniadmin iau
    // compania din token (backend-ul forțează company_id pentru rolurile scoped).
    isAdmin() {
      return getRolesSetFromStorage().has("admin");
    },
    // Comutator de vizualizare (carduri / tabel) pentru segmented control
    viewItems() {
      return [
        { value: "cards", label: this.$t("candidates.view.cards"), icon: "mdi-view-grid-outline" },
        { value: "list", label: this.$t("candidates.view.table"), icon: "mdi-view-list-outline" },
      ];
    },
    // opțiuni distincte pentru autocomplete
    optionsProfession() {
      return this.distinctOptionsFrom('Profession');
    },
    optionsSpecialization() {
      return this.distinctOptionsFrom('Specialization');
    },
    optionsLookingPosition() {
      return this.distinctOptionsFrom('LookingPosition');
    },
    optionsCVSource() {
      return this.distinctOptionsFrom('CVSource');
    },

    // chips afișate sub toolbar
    activeChips() {
      const chips = [];
      const pushArr = (arr, type, label) => {
        (arr || []).forEach(v => chips.push({ key: type + ':' + v, type, value: v, label: `${label}: ${v}` }));
      };
      pushArr(this.filters.profession, 'profession', this.$t("candidates.filters.profession"));
      pushArr(this.filters.specialization, 'specialization', this.$t("candidates.filters.specialization"));
      pushArr(this.filters.lookingPosition, 'lookingPosition', this.$t("candidates.filters.lookingPosition"));
      pushArr(this.filters.cvSource, 'cvSource', this.$t("candidates.filters.cvSource"));
      if (this.filters.contacted !== null) chips.push({ key: 'contacted', type: 'contacted', value: this.filters.contacted, label: `${this.$t("candidates.filters.contacted")}: ${this.filters.contacted ? this.$t("candidates.values.yes") : this.$t("candidates.values.no")}` });
      if (this.filters.hasCV !== null) chips.push({ key: 'hasCV', type: 'hasCV', value: this.filters.hasCV, label: `${this.$t("candidates.filters.hasCv")}: ${this.filters.hasCV ? this.$t("candidates.values.yes") : this.$t("candidates.values.no")}` });
      return chips;
    },
    activeFiltersCount() {
      return this.activeChips.length;
    },

    // filtrare principală (client-side)
    filteredData() {
      let data = this.candidatesData;

      // Căutare globală
      if (this.search) {
        const term = this.search.toLowerCase();
        data = data.filter(item =>
          Object.values(item).some(val => String(val).toLowerCase().includes(term))
        );
      }

      // Filtre multi-select (exact match, case-insensitive)
      const inListCI = (val, list) => {
        const s = String(val || '').toLowerCase();
        return list.map(v => String(v).toLowerCase()).includes(s);
      };
      if (this.filters.profession.length) {
        data = data.filter(it => inListCI(it.Profession, this.filters.profession));
      }
      if (this.filters.specialization.length) {
        data = data.filter(it => inListCI(it.Specialization, this.filters.specialization));
      }
      if (this.filters.lookingPosition.length) {
        data = data.filter(it => inListCI(it.LookingPosition, this.filters.lookingPosition));
      }
      if (this.filters.cvSource.length) {
        data = data.filter(it => inListCI(it.CVSource, this.filters.cvSource));
      }

      // Tri-state booleans
      if (this.filters.contacted !== null) {
        data = data.filter(it => Boolean(it.CandidateContacted) === this.filters.contacted);
      }
      if (this.filters.hasCV !== null) {
        data = data.filter(it => Boolean(it.HasCV) === this.filters.hasCV);
      }

      return data;
    }
  },
  created() {
    this.syncFromRoute();
    this.fetchCandidates();
    if (this.isAdmin) this.fetchCompanies();
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        if (this.applyingFromRoute) return;
        this.page = 1;
        this.persistStateDebounced();
      }
    },
    search() {
      if (this.applyingFromRoute) return;
      this.page = 1;
      this.persistStateDebounced();
    },
    viewMode() {
      if (this.applyingFromRoute) return;
      this.persistStateDebounced();
    },
    page() {
      if (this.applyingFromRoute) return;
      if (this.viewMode === 'list') this.persistStateDebounced();
    },
    itemsPerPage() {
      if (this.applyingFromRoute) return;
      if (this.viewMode === 'list') this.persistStateDebounced();
    },
    '$route.query'() {
      if (!this.syncingRoute) {
        this.syncFromRoute();
      }
    }
  },
  methods: {
    // ---- PERSISTENȚĂ FILTRE ÎN URL + localStorage ----
    buildQueryFromState() {
      const q = {};
      if (this.search) q.q = this.search;
      if (this.filters.profession.length) q.prof = this.filters.profession.join(',');
      if (this.filters.specialization.length) q.spec = this.filters.specialization.join(',');
      if (this.filters.lookingPosition.length) q.pos = this.filters.lookingPosition.join(',');
      if (this.filters.cvSource.length) q.src = this.filters.cvSource.join(',');
      if (this.filters.contacted !== null) q.contacted = String(this.filters.contacted);
      if (this.filters.hasCV !== null) q.hascv = String(this.filters.hasCV);
      if (this.viewMode && this.viewMode !== 'cards') q.vm = this.viewMode; // default 'cards' -> omit
      if (this.viewMode === 'list') {
        if (this.page && this.page !== 1) q.pg = String(this.page);
        if (this.itemsPerPage && this.itemsPerPage !== 10) q.ipp = String(this.itemsPerPage);
      }
      return q;
    },
    applyStateFromQuery(query) {
      this.applyingFromRoute = true;
      const parseList = (v) => (typeof v === 'string' && v.length) ? v.split(',') : [];
      const parseTri = (v) => v === 'true' ? true : v === 'false' ? false : null;
      const toNum = (v, def) => (v !== undefined && !isNaN(Number(v))) ? Number(v) : def;

      if (query.q !== undefined) this.search = String(query.q);
      this.filters.profession = parseList(query.prof);
      this.filters.specialization = parseList(query.spec);
      this.filters.lookingPosition = parseList(query.pos);
      this.filters.cvSource = parseList(query.src);
      this.filters.contacted = parseTri(query.contacted);
      this.filters.hasCV = parseTri(query.hascv);
      this.viewMode = (query.vm === 'list') ? 'list' : 'cards';
      this.page = toNum(query.pg, 1);
      this.itemsPerPage = toNum(query.ipp, 10);
      this.$nextTick(() => { this.applyingFromRoute = false; });
    },
    syncToRoute(replace = true) {
      const q = this.buildQueryFromState();
      this.syncingRoute = true;
      const nav = { query: q };
      const p = replace ? this.$router.replace(nav) : this.$router.push(nav);
      Promise.resolve(p).catch(() => {}).finally(() => { this.syncingRoute = false; });
    },
    syncFromRoute() {
      const q = this.$route && this.$route.query ? this.$route.query : {};
      // Dacă nu avem nimic în query, încearcă localStorage
      if (!Object.keys(q).length) {
        try {
          const saved = JSON.parse(localStorage.getItem('CandidatiFiltersV1') || '{}');
          const savedVm = localStorage.getItem('CandidatiViewModeV1');
          const savedQ = localStorage.getItem('CandidatiSearchV1');
          const savedPg = localStorage.getItem('CandidatiPageV1');
          const savedIpp = localStorage.getItem('CandidatiIppV1');
          this.applyStateFromQuery({ ...saved, vm: savedVm || undefined, q: savedQ || undefined, pg: savedPg || undefined, ipp: savedIpp || undefined });
        } catch { /* ignore */ }
        return;
      }
      this.applyStateFromQuery(q);
    },
    persistStateDebounced() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const q = this.buildQueryFromState();
        try {
          localStorage.setItem('CandidatiFiltersV1', JSON.stringify(q));
          localStorage.setItem('CandidatiViewModeV1', q.vm || 'cards');
          localStorage.setItem('CandidatiSearchV1', q.q || '');
          localStorage.setItem('CandidatiPageV1', String(this.page || 1));
          localStorage.setItem('CandidatiIppV1', String(this.itemsPerPage || 10));
        } catch { /* ignore */ }
        this.syncToRoute(true);
      }, 300);
    },

    distinctOptionsFrom(field) {
      const set = new Set(
        (this.candidatesData || [])
          .map(x => (x && x[field]) ? String(x[field]).trim() : '')
          .filter(Boolean)
      );
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },
    applyFilters() {
      this.filtersDrawer = false; // filtrarea e reactivă
      this.page = 1;
      this.persistStateDebounced();
    },
    resetFilters() {
      this.filters = { profession: [], specialization: [], lookingPosition: [], cvSource: [], contacted: null, hasCV: null };
      this.page = 1;
    },
    removeFilterChip(chip) {
      if (chip.type === 'profession') this.filters.profession = this.filters.profession.filter(v => v !== chip.value);
      else if (chip.type === 'specialization') this.filters.specialization = this.filters.specialization.filter(v => v !== chip.value);
      else if (chip.type === 'lookingPosition') this.filters.lookingPosition = this.filters.lookingPosition.filter(v => v !== chip.value);
      else if (chip.type === 'cvSource') this.filters.cvSource = this.filters.cvSource.filter(v => v !== chip.value);
      else if (chip.type === 'contacted') this.filters.contacted = null;
      else if (chip.type === 'hasCV') this.filters.hasCV = null;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return dateString.split("T")[0];
    },
    goToCandidateDetails(id) {
      this.$router.push({ name: "CandidateDetails", params: { id } });
    },
    openEditDialog(item) {
      this.isEdit = true;
      this.editCandidate = { ...item, Dateofbirth: this.formatDate(item.Dateofbirth) };
      this.editDialog = true;
    },
    openAddDialog() {
      this.isEdit = false;
      this.editCandidate = createEmptyCandidate();
      this.editDialog = true;
    },
    onDialogSave(form) {
      if (this.isEdit) {
        this.updateCandidate(form);
      } else {
        this.addCandidate(form);
      }
    },
    notify(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    addCandidate(form) {
      const data = { ...form };
      delete data.id;
      axios.post("/candidati/candidats", data)
        .then(() => {
          this.notify(this.$t("candidates.alerts.addSuccess"), "success");
          this.editDialog = false;
          this.fetchCandidates();
        })
        .catch(err => console.error("Eroare la adăugare:", err));
    },
    updateCandidate(form) {
      if (!form.id) return this.notify(this.$t("candidates.alerts.idInvalid"), "error");
      axios.put(`/candidati/candidats/${form.id}`, form)
        .then(() => {
          this.notify(this.$t("candidates.alerts.updateSuccess"), "success");
          this.editDialog = false;
          this.fetchCandidates();
        })
        .catch(err => console.error("Eroare la actualizare:", err));
    },
    confirmDeleteCandidate(id) {
      this.deleteId = id;
      this.deleteConfirmText = "";
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteId = null;
      this.deleteConfirmText = "";
    },
    performDelete() {
      if (this.deleteConfirmText !== "DELETE" || !this.deleteId) return;
      this.deleteCandidate(this.deleteId);
    },
    deleteCandidate(id) {
      axios.delete(`/candidati/candidats/${id}`)
        .then(() => {
          this.notify(this.$t("candidates.alerts.deleteSuccess"), "success");
          this.closeDeleteDialog();
          this.fetchCandidates();
        })
        .catch(err => console.error("Eroare la ștergere:", err));
    },
    fetchCandidates() {
      axios.get("/candidati/candidats")
        .then(resp => {
          this.candidatesData = (resp.data || [])
            .map(item => ({ ...item, Dateofbirth: this.formatDate(item.Dateofbirth) }))
            .sort((a, b) => b.id - a.id);
        })
        .catch(err => console.error("Eroare la fetch:", err));
    },
    fetchCompanies() {
      axios.get("/candidati/companies")
        .then(resp => { this.companies = Array.isArray(resp.data) ? resp.data : []; })
        .catch(err => console.error("Eroare la fetch companii:", err));
    },
  }
});
</script>

<style scoped>
/* Header curat de workspace (stil de referință: NcrmContractsHome). */
.candidati__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 2px 0 10px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  margin-bottom: 16px;
}
.candidati__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 0;
}
.candidati__header-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-tertiary));
  opacity: 0.85;
}
.candidati__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--app-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.candidati__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.6;
  white-space: nowrap;
  padding-left: 8px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  margin-left: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 960px) {
  .candidati__subtitle { display: none; }
}

/* Panou flat cu bordură subtilă (regula carduri de conținut). */
.candidati__panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
}
</style>
